import { useCallback, useState } from 'react';
import http from 'hub-http/clients/apiClient';
import styled from 'styled-components';
import debounce from 'transmute/debounce';
import getIn from 'immutable-less/transmute/getIn';
import { useQuery } from 'data-fetching-client';
import Raven from 'raven-js';
import { asHiddenProp } from 'UIComponents/utils/asHiddenProp';
import UISelect from 'UIComponents/input/UISelect';
import { getPropertyValue } from 'framework-listing-lib/utils/crmObject';
import FormattedMessageWithObjectTypeName from 'automation-ui-i18n/FormattedMessageWithObjectTypeName';
import { useGetSearchableFilterOptions } from 'automation-ui-log-events/hooks/crmSearchHooks';
import { CRM_OBJECT_SEARCH_BY_ID, CRM_OBJECT_SEARCH_BY_ID_FIELD_NAME } from 'automation-ui-log-events/api-client/queries/CrmSearchQueries';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledUISelect = styled(UISelect).withConfig({
  displayName: "CustomObjectFilterSelect__StyledUISelect",
  componentId: "hsq9fw-0"
})(["&&{max-width:400px;width:100% !important;}"]);
const CustomObjectFilterSelect = ({
  flowObjectTypeId,
  onChange,
  selectedObjectId,
  menuWidth,
  disabled,
  'data-test-id': dataTestId
}) => {
  const [optionsMap, setOptionsMap] = useState(new Map());
  const [storedSearchTerm, setStoredSearchTerm] = useState();
  const [shouldFetchInitialValue, setShouldFetchInitialValue] = useState(typeof selectedObjectId === 'number');
  const value = selectedObjectId ? String(selectedObjectId) : null;
  const {
    primaryDisplayLabelPropertyName,
    getSearchableFilterOptions
  } = useGetSearchableFilterOptions(flowObjectTypeId);
  const toOption = useCallback(crmObject => {
    const primaryDisplayLabel = getPropertyValue(crmObject, primaryDisplayLabelPropertyName);
    const id = getPropertyValue(crmObject, 'hs_object_id');
    return {
      text: primaryDisplayLabel,
      value: id
    };
  }, [primaryDisplayLabelPropertyName]);
  const getAccumulatedOptions = useCallback((nextOptions, shouldResetCache = false) => {
    const newOptionsMap = shouldResetCache ? new Map() : new Map(optionsMap);
    for (let i = 0; i < nextOptions.length; i++) {
      newOptionsMap.set(nextOptions[i].value, nextOptions[i]);
    }
    setOptionsMap(newOptionsMap);
    return [...newOptionsMap.values()];
  }, [optionsMap]);
  useQuery(CRM_OBJECT_SEARCH_BY_ID, {
    variables: {
      objectTypeId: flowObjectTypeId,
      objectId: selectedObjectId
    },
    skip: !shouldFetchInitialValue || !primaryDisplayLabelPropertyName,
    onCompleted: data => {
      const crmObject = getIn([CRM_OBJECT_SEARCH_BY_ID_FIELD_NAME], data);
      if (crmObject) {
        getAccumulatedOptions([toOption(crmObject)]);
      }
      setShouldFetchInitialValue(false);
    }
  });
  const handleSearch = useCallback(() => debounce(300, (input, callback, {
    offset
  } = {}) => {
    const count = 50;
    if (!primaryDisplayLabelPropertyName || !input.length) {
      callback(null);
      return;
    }
    http.post('crm-search/search', {
      data: Object.assign({
        objectTypeId: flowObjectTypeId,
        count,
        offset,
        query: '',
        requestOptions: {
          allPropertiesFetchMode: 'latest_version'
        },
        sorts: [{
          property: 'createdate',
          order: 'ASC'
        }]
      }, getSearchableFilterOptions(input))
    }).then(({
      results,
      hasMore,
      offset: nextOffset
    }) => {
      const newOptions = results.map(toOption);
      const inputChanged = input !== storedSearchTerm;
      if (inputChanged) {
        setStoredSearchTerm(input);
      }
      const accumulatedOptions = getAccumulatedOptions(newOptions, inputChanged);
      callback(null, {
        options: accumulatedOptions,
        pagination: {
          hasMore,
          offset: nextOffset
        }
      });
    }).catch(error => {
      callback(error);
      Raven.captureMessage('CustomObjectFilterSelect request failed: crm-search/search');
    });
  }), [primaryDisplayLabelPropertyName, flowObjectTypeId, getAccumulatedOptions, getSearchableFilterOptions, toOption, storedSearchTerm, setStoredSearchTerm])();
  return /*#__PURE__*/_jsx(StyledUISelect, {
    disabled: disabled,
    loadOptions: handleSearch,
    filterOptions: false,
    ignoreCase: asHiddenProp(false),
    ignoreAccents: asHiddenProp(false),
    onChange: onChange,
    placeholder: /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
      baseLangKey: "automation-ui-log-events.CustomObjectFilterSelect.placeholder_cob",
      objectTypeId: flowObjectTypeId
    }),
    value: value,
    menuWidth: menuWidth,
    "data-test-id": dataTestId
  });
};
export default CustomObjectFilterSelect;